import {
  PageConfig,
  PageConfigSchemaVersion,
} from "../../config/pageConfig.types";
import { DefaultFormId } from "../../config/resolveView";
import { halfWidth, thirdWidth } from "../../blocks/layout/columnSpans";
import { ContactAuditAgeField } from "./contactAuditRecordAdapter";

/**
 * The Contact Audit page's shipped layout: contact's, without the related tables.
 * Every field is read-only - an audit is a snapshot; a view can open fields up.
 */
export const contactAuditPageConfig: PageConfig = {
  schemaVersion: PageConfigSchemaVersion,
  entityName: "g4gdpr_contactaudit",
  viewId: DefaultFormId,
  pageType: "record",
  rows: [
    {
      id: "row-main",
      columns: [
        {
          id: "column-primary",
          span: { desktop: 9, tablet: 12, mobile: 12 },
          blocks: [
            {
              kind: "section",
              id: "section-personal-details",
              title: "Personal Details",
              helperText: "Basic identifying details for this contact.",
              fields: [
                {
                  id: "field-g4gdpr_salutation",
                  type: "text",
                  name: "g4gdpr_salutation",
                  label: "Title",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_firstname",
                  type: "text",
                  name: "g4gdpr_firstname",
                  label: "First Name",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_lastname",
                  type: "text",
                  name: "g4gdpr_lastname",
                  label: "Last Name",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_emailaddress1",
                  type: "text",
                  name: "g4gdpr_emailaddress1",
                  label: "Email Address",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_gendercode",
                  type: "radio",
                  name: "g4gdpr_gendercode",
                  label: "Gender",
                  readOnly: true,
                  span: thirdWidth,
                  options: [
                    { label: "Male", value: 1 },
                    { label: "Female", value: 2 },
                    { label: "Prefer not to say", value: null },
                  ],
                  inline: true,
                },
                {
                  id: "field-g4gdpr_birthdate",
                  type: "date",
                  name: "g4gdpr_birthdate",
                  label: "Date of Birth",
                  readOnly: true,
                  // Narrower than a third: birthdate and age pair up on one line.
                  span: { desktop: 2, tablet: 6, mobile: 6 },
                },
                {
                  id: "field-contactauditage",
                  type: "number",
                  name: ContactAuditAgeField,
                  label: "Age",
                  readOnly: true,
                  // Worked out from the birthdate on read; the audit has no age column.
                  excludeFromSave: true,
                  span: { desktop: 2, tablet: 6, mobile: 6 },
                },
                {
                  id: "field-g4gdpr_telephone1",
                  type: "text",
                  name: "g4gdpr_telephone1",
                  label: "Business Phone",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_telephone2",
                  type: "text",
                  name: "g4gdpr_telephone2",
                  label: "Home Phone",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_mobilephone",
                  type: "text",
                  name: "g4gdpr_mobilephone",
                  label: "Mobile Phone",
                  readOnly: true,
                  span: thirdWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-primary-address",
              title: "Primary Address",
              helperText: "The contact's main address.",
              fields: [
                {
                  id: "field-g4gdpr_address1_line1",
                  type: "text",
                  name: "g4gdpr_address1_line1",
                  label: "Line 1",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_address1_line2",
                  type: "text",
                  name: "g4gdpr_address1_line2",
                  label: "Line 2",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_address1_city",
                  type: "text",
                  name: "g4gdpr_address1_city",
                  label: "City",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_address1_county",
                  type: "text",
                  name: "g4gdpr_address1_county",
                  label: "County",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_address1_country",
                  type: "text",
                  name: "g4gdpr_address1_country",
                  label: "Country",
                  readOnly: true,
                  span: thirdWidth,
                },
                {
                  id: "field-g4gdpr_address1_postalcode",
                  type: "text",
                  name: "g4gdpr_address1_postalcode",
                  label: "Postal Code",
                  readOnly: true,
                  span: thirdWidth,
                },
              ],
            },
          ],
        },
        {
          id: "column-secondary",
          span: { desktop: 3, tablet: 12, mobile: 12 },
          card: true,
          fillHeight: true,
          blocks: [
            {
              kind: "section",
              id: "section-marketing-preferences",
              title: "Marketing Preferences",
              helperText: "Controls what this contact receives from the club.",
              fields: [
                {
                  id: "field-g4gdpr_donotbulkemail",
                  type: "boolean",
                  name: "g4gdpr_donotbulkemail",
                  label: "Club Email",
                  readOnly: true,
                  span: halfWidth,
                },
                {
                  id: "field-g4gdpr_g4c_donotallowsms",
                  type: "boolean",
                  name: "g4gdpr_g4c_donotallowsms",
                  label: "Club SMS",
                  readOnly: true,
                  span: halfWidth,
                },
                {
                  id: "field-g4gdpr_donotbulkpostalmail",
                  type: "boolean",
                  name: "g4gdpr_donotbulkpostalmail",
                  label: "Club Postal Mail",
                  readOnly: true,
                  span: halfWidth,
                },
                {
                  id: "field-g4gdpr_donotphone",
                  type: "boolean",
                  name: "g4gdpr_donotphone",
                  label: "Club Phone Calls",
                  readOnly: true,
                  span: halfWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-partner-preferences",
              title: "Partner Preferences",
              helperText: "Controls what this contact receives from the club's partners.",
              fields: [
                {
                  id: "field-g4gdpr_g4_donotbulkemail3rdparty",
                  type: "boolean",
                  name: "g4gdpr_g4_donotbulkemail3rdparty",
                  label: "Partner Email",
                  readOnly: true,
                  span: halfWidth,
                },
                {
                  id: "field-g4gdpr_g4_donotsms3rdparty",
                  type: "boolean",
                  name: "g4gdpr_g4_donotsms3rdparty",
                  label: "Partner SMS",
                  readOnly: true,
                  span: halfWidth,
                },
                {
                  id: "field-g4gdpr_g4_donotpostalmail3rdparty",
                  type: "boolean",
                  name: "g4gdpr_g4_donotpostalmail3rdparty",
                  label: "Partner Postal Mail",
                  readOnly: true,
                  span: halfWidth,
                },
                {
                  id: "field-g4gdpr_g4_donotphone3rdparty",
                  type: "boolean",
                  name: "g4gdpr_g4_donotphone3rdparty",
                  label: "Partner Phone Calls",
                  readOnly: true,
                  span: halfWidth,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
